import { defineStore, acceptHMRUpdate } from "pinia";
import { Notify } from "quasar";
import {
  isPreviewSceneDisplayRangeM,
  parseStoredPreviewSceneDisplayRangeM,
} from "@/constants/previewSceneDisplay";

const LS_PREVIEW_SCENE_DISPLAY_RANGE_M = "previewSceneDisplayRangeM";
const LS_SHOW_SUBSCRIBE_PLAYBACK_CONTROL = "domainControlShowSubscribePlaybackControl";
const LS_PRINT_SUBSCRIBE_DATA = "domainControlPrintSubscribeData";
const LS_ENABLE_MOCK = "domainControlEnableMock";
const LS_SHOW_HUD_SUS_IMU_ACC_VERT_CHART = "domainControlShowHudSusImuAccVertChart";

export const useDomainControlStore = defineStore({
  id: "domainControl",
  state: () => ({
    printLog: false,
    /** 是否在 useCarProperty 定时回调里打印即将传给 handleSubscribe 的 data（默认关，localStorage 持久化） */
    printSubscribeData:
      localStorage.getItem(LS_PRINT_SUBSCRIBE_DATA) === "true" || false,
    printInterval: false,
    notifyInterval: null as number | null,
    showVConsole: localStorage.getItem("showVConsole") === "true" || false,
    // 是否显示调试面板
    showDebugPanel: import.meta.env.VITE_NODE_ENV === "development",
    // 是否开启 mock 数据（订阅时用 mock 函数结果代替真实车属性；默认开，仅 localStorage 为 "false" 时关）
    enableMock: localStorage.getItem(LS_ENABLE_MOCK) !== "false",
    /** 路面预瞄页：仅 3D 场景前向展示距离（米）；HUD 雷达量程见 `PREVIEW_SCENE_HUD_RADAR_RANGE_M` */
    previewSceneDisplayRangeM: parseStoredPreviewSceneDisplayRangeM(
      localStorage.getItem(LS_PREVIEW_SCENE_DISPLAY_RANGE_M)
    ),
    // 调试用：当前页 useCarProperty 的订阅数据（showDebugPanel 为 true 时由 useCarProperty 写入）
    vehicleProperties: {} as Record<string, any>,
    // 录制相关
    recordList: [] as any[],
    isRecording: false,
    uploadRecordListLoading: false,
    /** 订阅数据是否处于「播放」；为 false 时 useCarProperty 50ms 回调整段不执行 */
    subscribePlaybackPlaying: true,
    /** 是否在预瞄页显示播放/暂停控制（仅 import.meta.env.DEV 时生效） */
    showSubscribePlaybackControl:
      localStorage.getItem(LS_SHOW_SUBSCRIBE_PLAYBACK_CONTROL) === "true" || false,
    /** 预瞄 HUD：是否显示垂向加速度 SusIMUAccVert 实时曲线（默认开，仅 localStorage 为 "false" 时关） */
    showHudSusImuAccVertChart:
      localStorage.getItem(LS_SHOW_HUD_SUS_IMU_ACC_VERT_CHART) !== "false",
  }),
  getters: {},
  actions: {
    setVehicleProperties(data: Record<string, any>) {
      this.vehicleProperties = { ...data };
    },
    changePrintLogStatus(status: boolean) {
      this.printLog = status;
    },
    changePrintSubscribeData(status: boolean) {
      this.printSubscribeData = status;
      localStorage.setItem(LS_PRINT_SUBSCRIBE_DATA, status.toString());
    },
    changePrintIntervalStatus(status: boolean) {
      this.printInterval = status;
    },
    changeNotifyInterval(status: number) {
      this.notifyInterval = status;
    },
    changeShowVConsole(status: boolean) {
      this.showVConsole = status;
      localStorage.setItem("showVConsole", status.toString());
    },
    changeShowDebugPanel(status: boolean) {
      this.showDebugPanel = status;
    },
    changeEnableMock(status: boolean) {
      this.enableMock = status;
      localStorage.setItem(LS_ENABLE_MOCK, status.toString());
    },
    changePreviewSceneDisplayRangeM(m: number) {
      if (!isPreviewSceneDisplayRangeM(m)) {
        return;
      }
      this.previewSceneDisplayRangeM = m;
      localStorage.setItem(LS_PREVIEW_SCENE_DISPLAY_RANGE_M, String(m));
    },
    toggleSubscribePlaybackPlaying() {
      this.subscribePlaybackPlaying = !this.subscribePlaybackPlaying;
    },
    setSubscribePlaybackPlaying(playing: boolean) {
      this.subscribePlaybackPlaying = playing;
    },
    changeShowSubscribePlaybackControl(status: boolean) {
      this.showSubscribePlaybackControl = status;
      localStorage.setItem(
        LS_SHOW_SUBSCRIBE_PLAYBACK_CONTROL,
        status.toString()
      );
    },
    changeShowHudSusImuAccVertChart(status: boolean) {
      this.showHudSusImuAccVertChart = status;
      localStorage.setItem(LS_SHOW_HUD_SUS_IMU_ACC_VERT_CHART, status.toString());
    },
    sentryTest() {
      const randomString = Math.random().toString(36).substring(2, 15);
      throw new Error("sentry test" + randomString + new Date().getTime());
    },
    // 开始录制
    startRecord() {
      this.isRecording = true;
      this.recordList = [];
      console.log("[BOWEN_LOG] 🚀 ~~ 开始录制数据");
    },
    // 更新录制列表
    updateRecordList(data: any) {
      if (this.isRecording) {
        this.recordList.push({ ...data });
      }
    },
    // 结束录制并发送数据
    async endRecord() {
      this.isRecording = false;
      console.log(
        "[BOWEN_LOG] 🚀 ~~ 停止录制数据，共录制",
        this.recordList.length,
        "条",
        this.recordList,
      );

      if (this.recordList.length > 0) {
        this.uploadRecordListLoading = true;
        try {
          // 将数据按照每1000条切割成多个块
          const chunkSize = 1000;
          const chunks: any[][] = [];
          for (let i = 0; i < this.recordList.length; i += chunkSize) {
            chunks.push(this.recordList.slice(i, i + chunkSize));
          }

          console.log(
            `[BOWEN_LOG] 🚀 ~~ 数据已切割成 ${chunks.length} 个块，每块最多 ${chunkSize} 条`
          );

          let successCount = 0;
          let failCount = 0;

          // 串行上传每个块
          for (let i = 0; i < chunks.length; i++) {
            const chunk = chunks[i];
            try {
              console.log(
                `[BOWEN_LOG] 🚀 ~~ 正在上传第 ${i + 1}/${chunks.length} 个块，包含 ${chunk.length} 条数据`
              );

              const response = await fetch(
                `${import.meta.env.VITE_FUXI_DATA_API}/api/fuxi-data/save-data`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    type: "preview-info",
                    data: chunk,
                  }),
                }
              );

              if (response.ok) {
                successCount++;
                console.log(
                  `[BOWEN_LOG] 🚀 ~~ 第 ${i + 1} 个块上传成功`
                );
              } else {
                failCount++;
                console.error(
                  `[BOWEN_LOG] 🚀 ~~ 第 ${i + 1} 个块上传失败:`,
                  response.status
                );
              }
            } catch (error) {
              failCount++;
              console.error(
                `[BOWEN_LOG] 🚀 ~~ 第 ${i + 1} 个块上传错误:`,
                error
              );
            }
          }

          // 显示最终结果
          if (failCount === 0) {
            console.log(
              `[BOWEN_LOG] 🚀 ~~ 所有数据发送成功，共 ${successCount} 个块`
            );
            Notify.create({
              type: "positive",
              message: `数据录制成功，共 ${this.recordList.length} 条，分 ${chunks.length} 个块上传`,
            });
          } else {
            console.error(
              `[BOWEN_LOG] 🚀 ~~ 数据发送完成，成功 ${successCount} 个块，失败 ${failCount} 个块`
            );
            Notify.create({
              type: "warning",
              message: `数据上传部分失败，成功 ${successCount} 个块，失败 ${failCount} 个块`,
            });
          }
        } catch (error) {
          console.error("[BOWEN_LOG] 🚀 ~~ 数据发送错误:", error);
          Notify.create({
            type: "negative",
            message: "数据上传失败，请重试",
          });
        } finally {
          this.uploadRecordListLoading = false;
        }
      } else {
        this.uploadRecordListLoading = false;
      }

      // 清空录制列表
      this.recordList = [];
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDomainControlStore, import.meta.hot)
  );
}
